/**
 * Routing Data Service
 *
 * Multi-tenant aware service for uploading routing data.
 * Routes uploads to tenant-specific datasets and storage paths.
 *
 * Storage: gs://{storageBucket}/tenants/{tenantId}/routing/{fileType}/
 * BigQuery: {projectId}.tenant_{tenantId}.{tableName}
 */

import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFirestore, collection, query, where, orderBy, limit, onSnapshot, doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { ROUTING_SCHEMAS, getRoutingSchema, getStoragePath } from '../config/routingSchemas';
import { FirebaseService } from '../../../shared/services/FirebaseService';

class RoutingDataServiceClass {
  constructor() {
    this.storage = null;
    this.functions = null;
    this.db = null;
    this.tenantId = null;

    console.log('🔧 RoutingDataService initialized');
  }

  /**
   * Set the current tenant context
   * @param {string} tenantId - The tenant ID (e.g., 'inmocean', 'acme')
   */
  setTenant(tenantId) {
    if (!tenantId) {
      throw new Error('tenantId is required');
    }
    this.tenantId = tenantId;
    console.log(`🏢 RoutingDataService tenant set to: ${tenantId}`);
  }

  /**
   * Get current tenant ID
   */
  getTenantId() {
    if (!this.tenantId) {
      throw new Error('Tenant not set. Call setTenant(tenantId) first.');
    }
    return this.tenantId;
  }

  /**
   * Initialize Firebase service connections
   */
  async initialize() {
    try {
      if (!this.storage) {
        this.storage = getStorage();
        console.log('✅ Routing storage initialized');
      }
      if (!this.functions) {
        this.functions = getFunctions();
        console.log('✅ Routing functions initialized');
      }
      if (!this.db) {
        this.db = getFirestore();
        console.log('✅ Routing Firestore initialized');
      }
    } catch (error) {
      console.error('❌ Failed to initialize Firebase services:', error);
      throw error;
    }
  }

  /**
   * Upload file to tenant-specific storage and trigger processing
   * @param {File} file - The file to upload
   * @param {string} fileType - The routing file type (e.g., 'routing_walmart_tms')
   * @param {Array} preValidatedData - Optional pre-validated data from frontend
   */
  async uploadAndProcessFile(file, fileType, preValidatedData = null) {
    const tenantId = this.getTenantId();

    try {
      console.log(`🚀 Starting upload for tenant ${tenantId}, fileType: ${fileType}:`, file.name);
      await this.initialize();

      // Get tenant-specific schema
      const schema = getRoutingSchema(fileType, tenantId, FirebaseService.getProjectId());

      // Upload to tenant-specific storage path
      const storageUrl = await this.uploadToStorage(file, fileType, tenantId);

      // Log the upload job to Firestore for tracking
      await this.logUploadJob(tenantId, fileType, file.name, storageUrl);

      const result = {
        tenantId,
        fileName: file.name,
        fileType,
        rowCount: preValidatedData ? preValidatedData.length : 0,
        uploadTime: new Date().toISOString(),
        fileSize: file.size,
        storageUrl: storageUrl,
        targetDataset: schema.datasetId,
        targetTable: schema.tableName,
        status: 'processing'
      };

      console.log(`✅ File uploaded for tenant ${tenantId}, fileType ${fileType}:`, result);
      return result;

    } catch (error) {
      console.error(`❌ Upload failed for tenant ${tenantId}, fileType ${fileType}:`, error);
      throw error;
    }
  }

  /**
   * Upload file to Google Cloud Storage with tenant isolation
   */
  async uploadToStorage(file, fileType, tenantId) {
    try {
      if (!this.storage) {
        await this.initialize();
      }

      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const storagePath = getStoragePath(tenantId, fileType);
      const filename = `${storagePath}/${timestamp}_${file.name}`;
      const storageRef = ref(this.storage, filename);

      console.log(`📤 Uploading to tenant storage: ${filename}`);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);

      console.log(`✅ File uploaded to tenant storage: ${downloadURL}`);
      return downloadURL;

    } catch (error) {
      console.error('❌ Storage upload failed:', error);
      throw new Error(`Storage upload failed: ${error.message}`);
    }
  }

  /**
   * Log upload job to Firestore for tracking and billing
   */
  async logUploadJob(tenantId, fileType, fileName, storageUrl) {
    try {
      if (!this.db) {
        await this.initialize();
      }

      const jobId = `${tenantId}_${fileType}_${Date.now()}`;
      const jobRef = doc(this.db, 'routing_jobs', jobId);

      await setDoc(jobRef, {
        tenantId,
        fileType,
        fileName,
        storageUrl,
        status: 'uploaded',
        startTime: serverTimestamp(),
        app: 'routing'
      });

      console.log(`📝 Upload job logged: ${jobId}`);
      return jobId;

    } catch (error) {
      console.warn('⚠️ Failed to log upload job (non-critical):', error);
      // Don't throw - this is non-critical for the upload itself
    }
  }

  /**
   * Listen for real-time updates on routing uploads for this tenant
   */
  listenToUploadHistory(callback) {
    const tenantId = this.getTenantId();
    this.initialize();

    const jobsRef = collection(this.db, 'routing_jobs');
    const q = query(
      jobsRef,
      where('tenantId', '==', tenantId),
      where('app', '==', 'routing'),
      orderBy('startTime', 'desc'),
      limit(50)
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const history = {};

      // Initialize all routing file types
      Object.keys(ROUTING_SCHEMAS).forEach(ft => {
        history[ft] = { status: 'empty', rowCount: null, lastUpload: null };
      });

      // Update with actual job data
      querySnapshot.forEach((doc) => {
        const job = doc.data();
        if (!history[job.fileType]?.lastUpload ||
            (job.startTime?.toDate() > new Date(history[job.fileType].lastUpload))) {
          history[job.fileType] = {
            lastUpload: job.startTime?.toDate()?.toISOString() || null,
            rowCount: job.details?.rowsInserted || job.rowCount || null,
            status: job.status === 'complete' ? 'active' : job.status,
            fileName: job.fileName,
            error: job.status === 'error' ? job.details?.error : null
          };
        }
      });

      console.log(`🔄 Routing upload history updated for tenant ${tenantId}`);
      callback(history);
    }, (error) => {
      console.error('❌ Error listening to routing upload history:', error);
    });

    return unsubscribe;
  }

  /**
   * Track usage for billing purposes
   */
  async trackUsage(usageType, amount, metadata = {}) {
    const tenantId = this.getTenantId();

    try {
      if (!this.db) {
        await this.initialize();
      }

      const now = new Date();
      const monthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      const usageRef = doc(this.db, `tenants/${tenantId}/usage`, monthKey);

      // This would use FieldValue.increment in production
      // For now, just log it
      console.log(`📊 Usage tracked for tenant ${tenantId}: ${usageType} = ${amount}`, metadata);

    } catch (error) {
      console.warn('⚠️ Failed to track usage (non-critical):', error);
    }
  }

  /**
   * Get tenant's dataset information
   */
  getTenantDatasetInfo() {
    const tenantId = this.getTenantId();
    return {
      tenantId,
      datasetId: `tenant_${tenantId}`,
      projectId: FirebaseService.getProjectId(),
      storageBucket: FirebaseService.getStorageBucket(),
      storagePath: `tenants/${tenantId}/routing`
    };
  }
}

// Export singleton instance
export const RoutingDataService = new RoutingDataServiceClass();

// Also export class for testing
export { RoutingDataServiceClass };
